import { UseMutateFunction } from "@tanstack/react-query";
import { SortChangedEvent } from "ag-grid-community";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";

import { unsafeWireToNumber } from "@performyard/money";
import { PYColumnDef } from "@performyard/py-components";
import { InputText, Table, type TableRef } from "@performyard/py-components";

import Pagination from "../../components/Pagination";

import { useEmployeeSelectionObject } from "../../hooks/useEmployeeSelectionObject";

import {
	GetUserSelectionUsersProps,
	RemoveUsersFromSelectionProps,
	UserSelectionMutationResponse,
	UserSelectionUsersResponse,
} from "../../api/services/defs/user-selection";

import AvatarGroup from "../AvatarGroup";
import Spacer from "../Spacer";

type RemoveUsersFunction = UseMutateFunction<
	UserSelectionMutationResponse,
	Error,
	RemoveUsersFromSelectionProps,
	unknown
>;

type SelectedEmployeesProps = {
	selectionObjectID: string;
	customFieldsIdentifiers: string[];
	getCustomColumnDefs: (props: {
		loadingStates: { isRemovingUsersFromSelection: boolean };
		actions: {
			removeUsersFromSelection: RemoveUsersFunction;
		};
	}) => PYColumnDef<UserSelectionUsersResponse["users"][number]>[];
	exclusionList: string[];
	filters: { [key: string]: string | Array<string> };
	height: string;
	onRemoveSuccess?: (data: UserSelectionMutationResponse) => void;
	onRemoveError?: (error: Error) => void;
	/**
	 * Optional custom remove handler that overrides the default removeUsersFromSelection.
	 * Use this when removal needs to go through a different API (e.g., survey participants
	 * that need to trigger ETL).
	 */
	customRemoveHandler?: RemoveUsersFunction;
};

const DEFAULT_CUSTOM_FIELD_NAMES = ["Manager", "Group"];

/**
 * Simplified, "read-only" version of the EmployeeSelection component that only shows the selected employees.
 */
const SelectedEmployees = ({
	selectionObjectID,
	customFieldsIdentifiers = DEFAULT_CUSTOM_FIELD_NAMES,
	getCustomColumnDefs = () => [],
	exclusionList = [],
	filters = {},
	height = "60dvh",
	onRemoveSuccess,
	onRemoveError,
	customRemoveHandler,
}: SelectedEmployeesProps) => {
	const ref = useRef<TableRef<UserSelectionUsersResponse["users"][number]>>(null);
	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearchTerm] = useDebounceValue(searchTerm, 300);

	const [queryProps, setQueryProps] = useState<GetUserSelectionUsersProps>({
		selectionObjectID: selectionObjectID,
		page: 1,
		searchTerm: "",
		exclusionList: exclusionList,
		filters: filters,
		sortField: "name",
		sortDirection: "asc",
		limitSelected: true,
	});

	useEffect(() => {
		refetchUsers();
	}, [selectionObjectID]);

	// Update queryProps when debouncedSearchTerm changes
	useEffect(() => {
		setQueryProps((prev) => ({
			...prev,
			searchTerm: debouncedSearchTerm,
			page: 1, // Reset to first page when search changes
		}));
	}, [debouncedSearchTerm]);

	const {
		data,
		usersList,
		isLoading,
		isFetching,
		initializedLoad,
		isRemovingUsersFromSelection,
		removeUsersFromSelection,
		refetchUsers,
	} = useEmployeeSelectionObject({
		...queryProps,
		onRemoveSuccess,
		onRemoveError,
	});

	const { selected_users = {}, departments = [], custom_fields = [], display_user_map = {} } = data || {};
	const pageCount = data?.page_count ?? 0;

	const [selectedCustomFieldsColumns] = useMemo(() => {
		const fullCustomFieldList = custom_fields.map((field) => {
			return {
				headerName: field.name,
				_id: field._id,
				type: field.type,
			};
		});

		const groupsMap = departments.reduce((acc, department) => {
			acc[department._id] = department.name;
			return acc;
		}, {});
		const selectedCustomFieldsColumns = customFieldsIdentifiers
			.map((identifier) => {
				return fullCustomFieldList.find((field) => field.headerName === identifier || field._id === identifier);
			})
			.filter((field) => field !== undefined)
			.map((field) => {
				const { _id, headerName, type } = field;
				switch (type) {
					case "person":
						return {
							headerName: headerName,
							type: "employee",
							flex: 1,
							sortable: true,
							colId: _id,
							valueGetter: (params) => {
								const manager = display_user_map[params.data.data_fields?.[_id]?.value];
								if (!manager) {
									return {
										_id: "",
										name: "",
									};
								}
								return {
									_id: params.data.data_fields?.[_id]?.value,
									name: manager?.name,
								};
							},
						};
					case "department":
						return {
							headerName: headerName,
							type: "text",
							flex: 1,
							sortable: true,
							colId: _id,
							valueGetter: (params) => {
								const group = params.data.data_fields?.[_id]?.value;
								const groupName = groupsMap[group];
								if (!group) {
									return "";
								}

								return groupName;
							},
						};
					case "people":
						return {
							headerName: headerName,
							flex: 1,
							sortable: true,
							colId: _id,
							cellRenderer: (params) => {
								const peopleIds = params.data.data_fields?.[_id]?.value;
								if (!peopleIds || !Array.isArray(peopleIds)) {
									return null;
								}

								// Map people IDs to employee objects
								const employees = peopleIds
									.map((personId) => {
										const person = display_user_map[personId];
										return person
											? {
													_id: personId,
													name: person.name,
												}
											: null;
									})
									.filter((employee): employee is { _id: string; name: string } => employee !== null) // Remove null entries
									.slice(0, 5); // Cap at 5 as requested

								if (employees.length === 0) {
									return null;
								}

								return (
									<AvatarGroup
										employees={employees}
										maxLength={5}
										employeeNameTooltip={true}
										hasLinks={true}
										canShowModal={true}
									/>
								);
							},
						};
					case "date":
						return {
							headerName: headerName,
							flex: 1,
							sortable: true,
							colId: _id,
							type: "date",
							valueGetter: (params) => {
								const value = params.data.data_fields?.[_id]?.value;
								return value || "";
							},
						};
					case "salary":
					case "monetary":
						return {
							headerName: headerName,
							flex: 1,
							sortable: true,
							colId: _id,
							valueGetter: (params) => {
								// Money values arrive as extended JSON ({ $numberDecimal: "85000.50" })
								// or as legacy plain numbers; display-only Intl formatting.
								const value = unsafeWireToNumber(params.data.data_fields?.[_id]?.value);
								if (value === null) return "";
								return new Intl.NumberFormat("en-US", {
									style: "currency",
									currency: "USD",
								}).format(value);
							},
						};
					case "numeric":
						return {
							headerName: headerName,
							flex: 1,
							sortable: true,
							colId: _id,
							valueGetter: (params) => {
								const value = params.data.data_fields?.[_id]?.value;
								if (!value && value !== 0) return "";
								return new Intl.NumberFormat("en-US").format(Number(value));
							},
						};
					case "phone":
						return {
							headerName: headerName,
							flex: 1,
							sortable: true,
							colId: _id,
							valueGetter: (params) => {
								const value = params.data.data_fields?.[_id]?.value;
								return value || "";
							},
						};
					case "single-select":
					case "multi-select":
						return {
							headerName: headerName,
							flex: 1,
							sortable: true,
							colId: _id,
							valueGetter: (params) => {
								const value = params.data.data_fields?.[_id]?.value;
								if (!value) return "";
								return Array.isArray(value) ? value.join(", ") : value;
							},
						};
					case "textbox":
						return {
							headerName: headerName,
							flex: 1,
							sortable: true,
							colId: _id,
							cellRenderer: (params) => {
								const value = params.data.data_fields?.[_id]?.value;
								if (!value) return "";
								// Truncate long text and show tooltip on hover
								const truncated = value.length > 50 ? `${value.substring(0, 47)}...` : value;
								return (
									<span title={value} className="py-line-clamp-2">
										{truncated}
									</span>
								);
							},
						};
					default:
						return {
							headerName: headerName,
							type: "text",
							flex: 1,
							valueGetter: (params) => {
								const isArray = Array.isArray(params.data.data_fields?.[_id]?.value);
								if (isArray) {
									return params.data.data_fields?.[_id]?.value
										.map((value: any) => value.name)
										.join(", ");
								}
								return params.data.data_fields?.[_id]?.value || "";
							},
						};
				}
			});

		return [selectedCustomFieldsColumns as PYColumnDef<UserSelectionUsersResponse["users"][number]>[]];
	}, [initializedLoad]);

	// Use custom remove handler if provided, otherwise use the default
	const effectiveRemoveHandler = customRemoveHandler ?? removeUsersFromSelection;

	// Create stable column definitions (no reactive dependencies)
	const columnDefs = useMemo<PYColumnDef<UserSelectionUsersResponse["users"][number]>[]>(() => {
		const customColumnDefs = getCustomColumnDefs({
			loadingStates: { isRemovingUsersFromSelection },
			actions: { removeUsersFromSelection: effectiveRemoveHandler },
		});
		return [
			{
				headerName: "Name",
				sortable: true,
				colId: "name",
				type: "employee",
				flex: 1,
				valueGetter: (params) => {
					return {
						_id: params.data?._id || "",
						name: params.data?.name || "",
					};
				},
			},
			...selectedCustomFieldsColumns,
			...customColumnDefs,
		];
	}, [selectedCustomFieldsColumns, effectiveRemoveHandler]); // Only depends on column structure

	// Create context object that updates when selection state changes
	const gridContext = useMemo(
		() => ({
			selected_users,
			selectionObjectID,
		}),
		[selected_users, selectionObjectID]
	);

	const onSortChanged = (sort: SortChangedEvent) => {
		if (sort.columns && sort.columns[0]) {
			setQueryProps({
				...queryProps,
				sortField: sort.columns[0].getColId(),
				sortDirection: sort.columns[0].getSort() as string | undefined,
			});
		} else {
			setQueryProps({ ...queryProps, sortField: undefined, sortDirection: undefined });
		}
	};

	return (
		<div>
			<div id="header row" className="flex flex-wrap items-center justify-between gap-2">
				<div id="header row left" className="flex gap-2">
					<div className="w-72">
						<InputText
							value={searchTerm}
							onChange={(value) => setSearchTerm(value)}
							leftIcon={faMagnifyingGlass}
							placeholder="Search Employees..."
							clearable={searchTerm.length > 0}
							aria-label="Search selected employees"
						/>
					</div>
				</div>
			</div>
			<div id="selectionTable" className="mt-4 size-full">
				<div className="w-full" style={{ height: height }}>
					<Table
						columnDefs={columnDefs}
						rowData={usersList}
						ref={ref}
						loading={isLoading || isFetching}
						context={gridContext}
						onSortChanged={onSortChanged}
					/>
				</div>
				<Spacer height={2} />
				{pageCount >= 2 && (
					<Pagination
						pageCount={pageCount}
						currentPage={queryProps.page}
						onClick={(page) => setQueryProps({ ...queryProps, page })}
					/>
				)}
			</div>
		</div>
	);
};

SelectedEmployees.displayName = "SelectedEmployees";

export default SelectedEmployees;
